import { Feature_Group_Config, Feature_Type_Config, Query_Root } from 'src/@types/graphql'
import { SupportedLocale } from 'src/@types'
import { Option } from 'src/shared/form/control'
import { Client } from 'urql'
import { queryAllFeatureGroupConfigs } from 'src/screens/administration/configuration-management/features-configuration/feature-groups/featureGroupsConfigQueries'
import { CONFIGURATION_STATUS, CONFIGURATION_STATUS_TYPE, LEVEL, LEVEL_TYPE } from 'src/shared/constants/constants'
import { queryAllFeatureTypeConfigs } from 'src/screens/administration/configuration-management/features-configuration/feature-types/featureTypesConfigQueries'
import { sortBy } from 'lodash'

export type KapFactsheetLevelRequiresKpi = {
  [key in LEVEL_TYPE[number]]: string | null
}

export const initialKapFactsheetLevelRequiresKpi = Object.values(LEVEL).reduce((acc, level) => {
  acc[level] = null
  return acc
}, {} as KapFactsheetLevelRequiresKpi)

export const mapFeatureGroupsToOptions = (
  featureGroups: Feature_Group_Config[],
  getMessage: (key: string, params?: Array<string | number>) => string,
  locale: SupportedLocale,
): Option[] =>
  featureGroups.map((fgc) => {
    if (fgc.active) {
      return {
        label: fgc.names[locale],
        value: fgc.id,
      }
    }

    const inactiveLabel = getMessage('label.inactive')
    return {
      label: (fgc.names[locale] || '') + ` (${inactiveLabel})`,
      value: fgc.id,
      disabled: true,
    }
  })

export const canUpdateFeatureConfigStatus = (
  selectedStatus: CONFIGURATION_STATUS_TYPE,
  selectedFeatureTypeId: number,
  selectedFeatureGroupId: number | null,
  featureTypeOptions: Feature_Type_Config[],
  featureGroupOptions: Feature_Group_Config[],
): boolean => {
  const isActiveSelected = selectedStatus === CONFIGURATION_STATUS.ACTIVE

  if (isActiveSelected) {
    return (
      featureTypeOptions.some((featureType) => featureType.id === selectedFeatureTypeId && featureType.active) &&
      (selectedFeatureGroupId == null ||
        featureGroupOptions.some((featureGroup) => featureGroup.id === selectedFeatureGroupId && featureGroup.active))
    )
  }

  return true
}

export const fetchAllFeatureGroups = (urqlClient: Client, locale: SupportedLocale) => async () => {
  const { data, error } = await urqlClient
    .query<{
      feature_group_config: Query_Root['feature_group_config']
    }>(queryAllFeatureGroupConfigs, {})
    .toPromise()

  if (error || !data?.feature_group_config) {
    throw new Error('Resource not found')
  }

  const fetchedFeatureGroups = sortBy(data.feature_group_config, [`names.${locale}`])
  return { fetchedFeatureGroups }
}

export const fetchAllFeatureTypesForFeaturesForm = (urqlClient: Client, locale: SupportedLocale) => async () => {
  const { data, error } = await urqlClient
    .query<{
      feature_type_config: Query_Root['feature_type_config']
    }>(queryAllFeatureTypeConfigs, {})
    .toPromise()

  if (error || !data?.feature_type_config) {
    throw new Error('Resource not found')
  }

  const fetchedFeatureTypes = sortBy(data.feature_type_config, [`names.${locale}`])
  return { fetchedFeatureTypes }
}
